import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { analyzerHref, displayQueryFromSearch } from "@/lib/analyzeQuery";
import { Search, ScanLine, Upload, RefreshCw, Bell } from "lucide-react";
import { DataModeBadge } from "./DataModeBadge";

const SUGGESTIONS = {
  Products: ["Member's Mark Water 40pk", "Rubbermaid Brilliance 10pc", "Ozark Trail Tumbler 40oz"],
  Recent: ["193968414788", "627242735"],
  "Saved Leads": ["Crayola Markers 40ct", "Hefty Slider Bags 120ct"],
  Actions: ["Analyze product", "Upload catalog", "Create watchlist"],
};

const ACTION_ROUTES = {
  "Upload catalog": "/app/catalog",
  "Create watchlist": "/app/leads/watchlist",
};

export function CommandBar({ title, breadcrumb, onOpenScan }) {
  const [spin, setSpin] = React.useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  const loc = useLocation();

  // Mirror analyzer URL into the sticky search when landing or re-searching.
  useEffect(() => {
    if (!loc.pathname.startsWith("/app/analyzer")) return;
    const fromUrl = displayQueryFromSearch(loc.search);
    if (fromUrl) setQ(fromUrl);
  }, [loc.pathname, loc.search]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goAnalyze = (term) => {
    const value = (term ?? q).trim();
    if (!value) {
      ref.current?.focus();
      setOpen(true);
      return;
    }
    const href = analyzerHref(value);
    if (!href) return;
    setQ(value);
    setDetecting(true);
    setOpen(false);
    // Navigate so ProductAnalyzer can load via api.product (live when params present).
    nav(href);
    // Spinner only for route transition feedback; analyzer shows its own skeleton.
    window.setTimeout(() => setDetecting(false), 280);
  };

  const runSuggestion = (it) => {
    if (it === "Analyze product") {
      goAnalyze(q);
      return;
    }
    const route = ACTION_ROUTES[it];
    if (route) {
      setOpen(false);
      nav(route);
      return;
    }
    setQ(it);
    goAnalyze(it);
  };

  const submit = () => goAnalyze(q);

  return (
    <header data-testid="command-bar"
      className="sticky top-0 z-20 h-14 ez-glass border-b border-edge-subtle/60 flex items-center gap-3 px-4 lg:px-6">
      <div className="flex-1 flex">
        <div className="relative w-full max-w-[560px] min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <input ref={ref} data-testid="command-search-input" value={q}
            onChange={(e) => setQ(e.target.value)} onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Search UPC, GTIN, Walmart Item ID, product or paste URL"
            className="w-full h-9 pl-9 pr-24 rounded-ctrl bg-surface-1 border border-edge-subtle text-compact
                       placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-signal-blue/40 focus:bg-surface-0" />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {detecting
              ? <RefreshCw className="h-3.5 w-3.5 text-signal-blue animate-spin" data-testid="cmd-search-loading" />
              : (
                <>
                  <button
                    type="button"
                    data-testid="cmd-analyze-btn"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={submit}
                    className="h-7 px-2 rounded-ctrl text-micro font-medium text-signal-blue hover:bg-signal-blue/10"
                  >
                    Analyze
                  </button>
                  <kbd className="text-micro font-mono text-ink-muted border border-edge-subtle rounded px-1 py-0.5">⌘K</kbd>
                </>
              )}
          </span>

          {open && (
            <div className="absolute top-11 left-0 right-0 bg-surface-0 border border-edge-subtle rounded-panel shadow-menu py-2 max-h-80 overflow-y-auto ez-scroll" data-testid="cmd-search-suggestions">
              {Object.entries(SUGGESTIONS).map(([grp, items]) => (
                <div key={grp} className="mb-1">
                  <div className="px-3 py-1 text-micro font-mono uppercase text-ink-muted">{grp}</div>
                  {items.map((it) => (
                    <button key={it} type="button" onMouseDown={(e) => { e.preventDefault(); runSuggestion(it); }}
                      className="w-full text-left px-3 py-1.5 text-compact text-ink-primary hover:bg-surface-2">
                      {it}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button data-testid="cmd-scan-btn" onClick={onOpenScan}
          className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">
          <ScanLine className="h-4 w-4" /> Scan
        </button>
        <button data-testid="cmd-upload-btn"
          className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">
          <Upload className="h-4 w-4" /> Upload
        </button>
        <button data-testid="cmd-refresh-btn" onClick={() => { setSpin(true); setTimeout(() => { setSpin(false); window.location.reload(); }, 500); }} className="h-9 w-9 grid place-items-center rounded-ctrl border border-edge-subtle hover:bg-surface-2 ez-press" title="Refresh data">
          <RefreshCw className={cn("h-4 w-4 text-ink-secondary", spin && "animate-spin")} />
        </button>
        <button data-testid="cmd-notifications" aria-label="Notifications" className="relative h-9 w-9 grid place-items-center rounded-ctrl border border-edge-subtle hover:bg-surface-2">
          <Bell className="h-4 w-4 text-ink-secondary" />
          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-red-600" />
        </button>
        <div className="hidden lg:block"><DataModeBadge compact /></div>
      </div>
    </header>
  );
}
